import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Category,
  Post,
  PostInput,
  PostStatus,
  PostSummary,
  Profile,
  ReviewAction,
} from '../types/content';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/** True when the app has been pointed at a deployed backend. */
export const isApiConfigured = Boolean(API_URL);

async function authHeader(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new ApiError(401, 'Your session expired. Please sign in again.');
  return { Authorization: `Bearer ${token}` };
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!API_URL) {
    throw new ApiError(
      0,
      'REACT_APP_API_URL is not set. Deploy the backend and add the API URL to your .env file.'
    );
  }

  const { method = 'GET', body, auth = false } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) Object.assign(headers, await authHeader());

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeJsonParse(text) : {};

  if (!response.ok) {
    const message =
      (payload as { error?: string; message?: string }).error ||
      (payload as { message?: string }).message ||
      `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message);
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

// --- Public ----------------------------------------------------------------

export const listPosts = (params: { category?: string; tag?: string } = {}) => {
  const query = new URLSearchParams();
  if (params.category) query.set('category', params.category);
  if (params.tag) query.set('tag', params.tag);
  const suffix = query.toString() ? `?${query}` : '';
  return request<{ posts: PostSummary[] }>(`/posts${suffix}`).then((r) => r.posts);
};

export const getPost = (slug: string) =>
  request<{ post: Post }>(`/posts/${encodeURIComponent(slug)}`).then((r) => r.post);

export const listCategories = () =>
  request<{ categories: Category[] }>('/categories').then((r) => r.categories);

// --- Signed in -------------------------------------------------------------

export const getProfile = () => request<Profile>('/me', { auth: true });

/**
 * Mirrors profile changes into MongoDB. Cognito is updated first by the caller
 * — that is what sends the verification code for a new email or phone number.
 */
export const updateProfile = (input: { name?: string; email?: string; phone?: string }) =>
  request<Profile>('/me', { method: 'PUT', body: input, auth: true });

// --- Admin -----------------------------------------------------------------

// --- Your own posts (any signed-in user) ------------------------------------

export const myListPosts = () =>
  request<{ posts: PostSummary[] }>('/my/posts', { auth: true }).then((r) => r.posts);

export const myGetPost = (id: string) =>
  request<{ post: Post }>(`/my/posts/${id}`, { auth: true }).then((r) => r.post);

export const myCreatePost = (input: PostInput) =>
  request<{ post: Post }>('/my/posts', { method: 'POST', body: input, auth: true }).then(
    (r) => r.post
  );

export const myUpdatePost = (id: string, input: Partial<PostInput>) =>
  request<{ post: Post }>(`/my/posts/${id}`, { method: 'PUT', body: input, auth: true }).then(
    (r) => r.post
  );

export const myDeletePost = (id: string) =>
  request<void>(`/my/posts/${id}`, { method: 'DELETE', auth: true });

// --- Admin ------------------------------------------------------------------

export const adminListPosts = (status?: PostStatus) =>
  request<{ posts: PostSummary[] }>(
    status ? `/admin/posts?status=${status}` : '/admin/posts',
    { auth: true }
  ).then((r) => r.posts);

/** approve / reject / deactivate / reactivate. `note` is required to reject. */
export const adminReviewPost = (id: string, action: ReviewAction, note?: string) =>
  request<{ post: Post }>(`/admin/posts/${id}/review`, {
    method: 'POST',
    body: { action, note },
    auth: true,
  }).then((r) => r.post);

export const adminGetPost = (id: string) =>
  request<{ post: Post }>(`/admin/posts/${id}`, { auth: true }).then((r) => r.post);

export const adminCreatePost = (input: PostInput) =>
  request<{ post: Post }>('/admin/posts', { method: 'POST', body: input, auth: true }).then(
    (r) => r.post
  );

export const adminUpdatePost = (id: string, input: Partial<PostInput>) =>
  request<{ post: Post }>(`/admin/posts/${id}`, { method: 'PUT', body: input, auth: true }).then(
    (r) => r.post
  );

export const adminDeletePost = (id: string) =>
  request<void>(`/admin/posts/${id}`, { method: 'DELETE', auth: true });

export const adminListCategories = () =>
  request<{ categories: Category[] }>('/admin/categories', { auth: true }).then(
    (r) => r.categories
  );

export const adminCreateCategory = (input: { name: string; description?: string }) =>
  request<{ category: Category }>('/admin/categories', {
    method: 'POST',
    body: input,
    auth: true,
  }).then((r) => r.category);

export const adminUpdateCategory = (
  id: string,
  input: { name?: string; description?: string; order?: number; slug?: string }
) =>
  request<{ category: Category }>(`/admin/categories/${id}`, {
    method: 'PUT',
    body: input,
    auth: true,
  }).then((r) => r.category);

export const adminDeleteCategory = (id: string) =>
  request<void>(`/admin/categories/${id}`, { method: 'DELETE', auth: true });

// --- Media -----------------------------------------------------------------

interface UploadTicket {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  contentType: string;
}

/**
 * Two-step upload: ask the API for a presigned URL, then PUT the bytes straight
 * to S3 so image data never travels through Lambda.
 */
export async function uploadImage(file: File, postId?: string): Promise<string> {
  const ticket = await request<UploadTicket>('/admin/media/upload-url', {
    method: 'POST',
    auth: true,
    body: {
      filename: file.name,
      contentType: file.type,
      size: file.size,
      // Groups the object under media/<author>/<post>/ so deleting the post
      // can remove its images in one prefix sweep.
      postId,
    },
  });

  const upload = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': ticket.contentType,
      'Cache-Control': 'public, max-age=31536000, immutable',
    },
    body: file,
  });

  if (!upload.ok) {
    throw new ApiError(upload.status, 'Upload to storage failed. Please try again.');
  }

  return ticket.publicUrl;
}
